<template>
  <div class="h-full rounded-lg flex flex-col gap-2">
    <div class="flex gap-2 h-full overflow-hidden">
      <!-- Left: equipamento table -->
      <div
        class="flex flex-col gap-2 overflow-y-auto scrollbar-thin scrollbar-tema transition-all duration-300"
        :class="selectedEquipamento ? 'w-2/5' : 'w-full'"
      >
        <EquipamentoFilter
          v-if="showFilter"
          @filtersChanged="updateFilters"
          @close-filter="showFilter = false"
        />

        <div class="h-full">
          <GenericDataTable
            ref="dataTableRef"
            :key="selectedEquipamento ? 'table-split' : 'table-full'"
            :api-url="`${baseUrl}/api/v1/equipamento/pagination/`"
            :useServerSide="true"
            :columns="columns"
            :initial-params="currentFilters"
            :show_export="false"
            :show_close_btn="false"
            table_title="Equipamentos"
            @row-click="onSelectEquipamento"
          >
            <template #left-actions>
              <button
                class="action-btn filter-btn"
                title="Filtros"
                @click="showFilter = !showFilter"
              >
                <i class="fa-solid fa-filter"></i>
              </button>
            </template>
          </GenericDataTable>
        </div>
      </div>

      <!-- Right: association panel -->
      <div
        v-if="selectedEquipamento"
        class="w-3/5 flex flex-col gap-2 overflow-hidden scrollbar-thin scrollbar-tema"
      >
        <!-- Header -->
        <div
          class="bg-[var(--fundoForm)] rounded-lg px-4 py-3 flex justify-between items-center shrink-0"
        >
          <div class="flex items-center gap-3">
            <div>
              <p class="text-xs text-gray-400 leading-none mb-0.5">
                {{ selectedEquipamento.codigo }}
              </p>
              <h3 class="text-[var(--text)] font-semibold text-sm">
                {{ selectedEquipamento.nome }}
              </h3>
            </div>
            <span
              class="text-xs px-2 py-0.5 rounded-full"
              :class="
                selectedEquipamento.status === 'ativo'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-600 text-gray-200'
              "
            >
              {{ selectedEquipamento.status }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              class="text-xs px-3 py-1 rounded-md transition-colors"
              :class="
                activeTab === tab.key
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              "
              @click="activeTab = tab.key"
            >
              {{ tab.label }}
            </button>
            <button
              class="text-gray-400 hover:text-[var(--text)] ml-2"
              @click="clearSelection"
            >
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
        </div>

        <!-- ── Associações tab ── -->
        <div
          v-if="activeTab === 'assoc'"
          class="flex flex-col gap-2 overflow-hidden flex-1"
        >
          <!-- Action bar: search + filter toggle + save -->
          <div
            class="bg-[var(--fundoForm)] rounded-lg px-4 py-2 flex flex-col gap-2 shrink-0"
          >
            <div class="flex items-center gap-2">
              <!-- Search -->
              <div class="relative flex-1">
                <input
                  v-model="tableSearch"
                  placeholder="Pesquisar equipamentos..."
                  class="w-full bg-[var(--fundoApp)] border border-gray-600 rounded pl-7 pr-3 py-1.5 text-[var(--text)] text-xs focus:border-blue-500 focus:outline-none"
                  @input="onTableSearch"
                />
              </div>

              <!-- Filter toggle -->
              <button
                class="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border transition-colors shrink-0"
                :class="
                  showTableFilters
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'border-gray-600 text-gray-400 hover:text-[var(--text)] hover:border-gray-400'
                "
                @click="showTableFilters = !showTableFilters"
              >
                <i class="fa-solid fa-filter"></i>
                <span
                  v-if="tableActiveFilterCount > 0"
                  class="bg-blue-400 text-white text-[10px] px-1.5 py-0.5 rounded-full leading-none"
                >
                  {{ tableActiveFilterCount }}
                </span>
              </button>

              <!-- Filter by selected equipamento -->
              <button
                class="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-gray-600 text-gray-400 hover:text-[var(--text)] hover:border-gray-400 transition-colors shrink-0"
                title="Filtrar por características deste equipamento"
                @click="applyEquipamentoFilters"
              >
                <i class="fa-solid fa-wand-magic-sparkles"></i>
              </button>

              <!-- Changes + save -->
              <template v-if="podeGerir && hasChanges">
                <span
                  v-if="toAssociate.length"
                  class="text-green-400 text-xs font-medium shrink-0"
                  >+{{ toAssociate.length }}</span
                >
                <span
                  v-if="toDisassociate.length"
                  class="text-red-400 text-xs font-medium shrink-0"
                  >-{{ toDisassociate.length }}</span
                >
                <button
                  class="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 shrink-0"
                  :disabled="savingChanges"
                  @click="saveChanges"
                >
                  <i class="fa-solid fa-floppy-disk"></i>
                  {{ savingChanges ? "A guardar..." : "Guardar" }}
                </button>
                <button
                  class="text-gray-500 hover:text-gray-300 text-xs shrink-0"
                  @click="resetChanges"
                >
                  Cancelar
                </button>
              </template>
              <span v-else class="text-gray-500 text-xs shrink-0"
                >{{ associatedCount }} associado(s)</span
              >
            </div>

            <!-- Filter panel (collapsible) -->
            <EquipamentoAssociarFilter
              v-show="showTableFilters"
              ref="assocFilter"
              :status-options="statusOptions"
              :familia-options="familiaOptions"
              :produto-options="produtoOptions"
              :tipologia-options="tipologiaOptions"
              :marca-options="marcaOptions"
              :medida-options="medidaOptions"
              :servico-options="servicoOptions"
              @filters-changed="onTableFiltersChanged"
            />
          </div>

          <!-- Checkbox table -->
          <div
            class="bg-[var(--fundoForm)] rounded-lg overflow-hidden flex flex-col flex-1"
          >
            <div
              class="grid grid-cols-[32px_110px_1fr_72px] gap-2 px-3 py-2 border-b border-gray-700 shrink-0"
            >
              <div class="flex items-center">
                <input
                  ref="headerCheckbox"
                  type="checkbox"
                  :checked="allLoadedChecked"
                  class="accent-blue-500 cursor-pointer"
                  @change="toggleAllLoaded"
                />
              </div>
              <span class="text-gray-400 text-xs font-medium">Código</span>
              <span class="text-gray-400 text-xs font-medium">Nome</span>
              <span class="text-gray-400 text-xs font-medium">Status</span>
            </div>

            <!-- Select-all-pages banner -->
            <div
              v-if="allLoadedChecked && hasMore && !selectAllMode"
              class="flex items-center justify-between px-4 py-2 bg-blue-900/20 border-b border-blue-700/30 shrink-0"
            >
              <span class="text-blue-300 text-xs">
                {{ tableItems.length }} itens visíveis selecionados.
              </span>
              <button
                class="text-blue-400 hover:text-blue-200 text-xs underline"
                :disabled="loadingAllIds"
                @click="selectAllPages"
              >
                {{
                  loadingAllIds
                    ? "A carregar..."
                    : `Selecionar todos os ${tableTotal + 1}`
                }}
              </button>
            </div>
            <div
              v-else-if="selectAllMode"
              class="flex items-center justify-between px-4 py-2 bg-blue-900/20 border-b border-blue-700/30 shrink-0"
            >
              <span class="text-blue-300 text-xs"
                >Todos os {{ tableTotal + 1 }} equipamentos selecionados.</span
              >
              <button
                class="text-gray-400 hover:text-[var(--text)] text-xs underline"
                @click="clearAllPages"
              >
                Limpar seleção
              </button>
            </div>

            <div class="overflow-y-auto scrollbar-thin scrollbar-tema flex-1">
              <div
                v-if="tableLoading && tableItems.length === 0"
                class="text-gray-500 text-xs text-center py-8"
              >
                A carregar...
              </div>
              <div
                v-for="item in tableItems"
                :key="item.id"
                class="grid grid-cols-[32px_110px_1fr_72px] gap-2 px-3 py-2 border-b border-gray-800/50 hover:bg-[var(--fundoApp)] cursor-pointer transition-colors"
                :class="isChecked(item.id) ? 'bg-blue-900/10' : ''"
                @click="toggleCheck(item.id)"
              >
                <div class="flex items-center">
                  <input
                    type="checkbox"
                    :checked="isChecked(item.id)"
                    :disabled="!podeGerir"
                    class="accent-blue-500 cursor-pointer disabled:cursor-not-allowed"
                    @click.stop
                    @change="toggleCheck(item.id)"
                  />
                </div>
                <span class="text-gray-400 text-xs truncate self-center">{{
                  item.codigo
                }}</span>
                <span class="text-[var(--text)] text-xs truncate self-center">{{
                  item.nome
                }}</span>
                <span
                  class="text-xs self-center px-1.5 py-0.5 rounded-full text-center w-fit"
                  :class="
                    item.status === 'ativo'
                      ? 'bg-green-600/30 text-green-400'
                      : 'bg-gray-600/30 text-gray-400'
                  "
                >
                  {{ item.status }}
                </span>
              </div>

              <div
                v-if="hasMore"
                class="flex items-center justify-center gap-4 py-3"
              >
                <button
                  class="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1.5"
                  :disabled="tableLoading"
                  @click="loadMore"
                >
                  <i
                    v-if="tableLoading"
                    class="fa-solid fa-spinner fa-spin"
                  ></i>
                  <i v-else class="fa-solid fa-chevron-down"></i>
                  {{
                    tableLoading
                      ? "A carregar..."
                      : `Carregar mais ${tablePageSize}`
                  }}
                </button>
                <span class="text-gray-700 text-xs">|</span>
                <button
                  class="text-gray-400 hover:text-[var(--text)] text-xs flex items-center gap-1.5"
                  :disabled="tableLoading"
                  @click="loadAll"
                >
                  <i
                    v-if="tableLoading"
                    class="fa-solid fa-spinner fa-spin"
                  ></i>
                  <i v-else class="fa-solid fa-angles-down"></i>
                  {{
                    tableLoading
                      ? "A carregar..."
                      : `Carregar todos (${
                          tableTotal - tableItems.length
                        } restantes)`
                  }}
                </button>
              </div>
              <div
                v-else-if="!tableLoading && tableItems.length === 0"
                class="text-gray-500 text-xs text-center py-8"
              >
                Nenhum equipamento encontrado
              </div>
            </div>
          </div>
        </div>

        <!-- ── Visibilidade tab ── -->
        <div
          v-if="activeTab === 'vis'"
          class="bg-[var(--fundoForm)] rounded-lg p-4 flex flex-col gap-3"
        >
          <div class="flex flex-col gap-2">
            <div
              v-for="field in mostrarFields"
              :key="field.key"
              class="flex justify-between items-center"
            >
              <span class="text-gray-300 text-xs">{{ field.label }}</span>
              <button
                :class="[
                  'toggle-btn',
                  mostrarValues[field.key] === 1 ? 'toggle-on' : 'toggle-off',
                ]"
                :disabled="!podeGerir"
                @click="toggleMostrar(field.key)"
              >
                {{ mostrarValues[field.key] === 1 ? "Sim" : "Não" }}
              </button>
            </div>
          </div>
          <button
            v-if="podeGerir"
            class="mt-2 w-full py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md transition-colors"
            :disabled="savingMostrar"
            @click="saveMostrar"
          >
            {{ savingMostrar ? "A guardar..." : "Guardar Visibilidade" }}
          </button>
          <p v-else class="mt-2 text-gray-500 text-xs text-center">
            Só de consulta: requer a valência "Gerir famílias e equipamentos".
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { toast } from "vue3-toastify";
import permissionMixin from "@/permissionMixin.js";
import { podeGerirFamiliasEquipamentos } from "@/utils/valencias";
import GenericDataTable from "@/components/dataTable/GenericDataTable2.vue";
import EquipamentoFilter from "@/components/filterDataTable/equipamentoFilter.vue";
import EquipamentoAssociarFilter from "@/components/filterDataTable/equipamentoAssociarFilter.vue";

export default {
  name: "equipamentosAssociarView",
  mixins: [permissionMixin],
  components: {
    GenericDataTable,
    EquipamentoFilter,
    EquipamentoAssociarFilter,
  },

  data() {
    return {
      baseUrl: process.env.VUE_APP_URL_BASE,
      showFilter: false,
      currentFilters: {},

      selectedEquipamento: null,
      activeTab: "assoc",
      tabs: [
        { key: "assoc", label: "Associações" },
        { key: "vis", label: "Visibilidade" },
      ],

      // Checkbox table
      tableItems: [],
      tableSearch: "",
      tablePage: 1,
      tablePageSize: 25,
      tableTotal: 0,
      tableLoading: false,
      searchTimeout: null,

      // Right-panel filters
      showTableFilters: false,
      tableFilters: {}, // set by EquipamentoAssociarFilter via onTableFiltersChanged
      tableActiveFilterCount: 0,
      // Metadata options (passed as props to EquipamentoAssociarFilter)
      statusOptions: [],
      familiaOptions: [],
      produtoOptions: [],
      tipologiaOptions: [],
      marcaOptions: [],
      medidaOptions: [],
      servicoOptions: [],

      // Association tracking
      originalAssociatedIds: new Set(),
      checkedIds: {},
      savingChanges: false,
      selectAllMode: false,
      loadingAllIds: false,

      // Sem a valência "Gerir famílias e equipamentos" o ecrã fica só de
      // consulta: o backend recusa as escritas, aqui não se mostram.
      podeGerir: false,

      // Visibilidade
      mostrarValues: {
        mostrar_levantamento: 1,
        mostrar_orcamento: 1,
        mostrar_financeiro: 1,
        mostrar_previsto: 1,
        mostrar_tecnico: 1,
      },
      mostrarFields: [
        { key: "mostrar_levantamento", label: "Levantamento" },
        { key: "mostrar_orcamento", label: "Orçamento" },
        { key: "mostrar_financeiro", label: "Financeiro" },
        { key: "mostrar_previsto", label: "Previsto" },
        { key: "mostrar_tecnico", label: "Técnico" },
      ],
      savingMostrar: false,

      columns: [
        { key: "familia.nome", label: "Família", type: "text", maxWidth: 90 },
        { key: "codigo", label: "Código", type: "text", maxWidth: 100 },
        { key: "nome", label: "Nome", type: "text", maxWidth: 200 },
        {
          key: "equipamento_associado.nome",
          label: "Associado",
          type: "text",
          maxWidth: 200,
        },
        { key: "status", label: "Status", type: "badge", maxWidth: 90 },
      ],
    };
  },

  computed: {
    hasMore() {
      return this.tableItems.length < this.tableTotal;
    },
    toAssociate() {
      return Object.keys(this.checkedIds)
        .filter(
          (id) =>
            this.checkedIds[id] && !this.originalAssociatedIds.has(Number(id)),
        )
        .map(Number);
    },
    toDisassociate() {
      return [...this.originalAssociatedIds].filter(
        (id) => !this.checkedIds[id],
      );
    },
    hasChanges() {
      return this.toAssociate.length > 0 || this.toDisassociate.length > 0;
    },
    associatedCount() {
      return Object.values(this.checkedIds).filter(Boolean).length;
    },
    allLoadedChecked() {
      return (
        this.tableItems.length > 0 &&
        this.tableItems.every((e) => !!this.checkedIds[e.id])
      );
    },
    someLoadedChecked() {
      return this.tableItems.some((e) => !!this.checkedIds[e.id]);
    },
  },

  watch: {
    someLoadedChecked() {
      this.$nextTick(() => {
        if (this.$refs.headerCheckbox) {
          this.$refs.headerCheckbox.indeterminate =
            this.someLoadedChecked && !this.allLoadedChecked;
        }
      });
    },
    allLoadedChecked() {
      this.$nextTick(() => {
        if (this.$refs.headerCheckbox) {
          this.$refs.headerCheckbox.indeterminate =
            this.someLoadedChecked && !this.allLoadedChecked;
        }
      });
    },
  },

  async mounted() {
    this.podeGerir = await podeGerirFamiliasEquipamentos();
    await this.loadFilterMetadata();
  },

  methods: {
    async loadFilterMetadata() {
      try {
        const res = await axios.get(
          `${this.baseUrl}/api/v1/equipamento/metadata/`,
        );
        const filters = res.data.data?.filters ?? [];
        const find = (key) => filters.find((f) => f.key === key)?.options ?? [];
        this.statusOptions = find("status");
        this.familiaOptions = find("familia_id");
        this.produtoOptions = find("produto_id");
        this.tipologiaOptions = find("tipologia_id");
        this.marcaOptions = find("tpMarca_id");
        this.medidaOptions = find("tpMedida_id");
        this.servicoOptions = find("tpServico_id");
      } catch {
        // filters won't show options, non-critical
      }
    },

    updateFilters(filters) {
      this.currentFilters = { ...filters };
      this.$refs.dataTableRef.refresh();
    },

    async onSelectEquipamento(row) {
      if (this.selectedEquipamento?.id === row.id) return;
      try {
        const res = await axios.get(
          `${this.baseUrl}/api/v1/equipamento/${row.id}/`,
        );
        const data = res.data.data;
        this.selectedEquipamento = data;
        this.activeTab = "assoc";
        this.mostrarValues = {
          mostrar_levantamento: data.mostrar_levantamento ?? 1,
          mostrar_orcamento: data.mostrar_orcamento ?? 1,
          mostrar_financeiro: data.mostrar_financeiro ?? 1,
          mostrar_previsto: data.mostrar_previsto ?? 1,
          mostrar_tecnico: data.mostrar_tecnico ?? 1,
        };
        await this.initAssociations();
      } catch {
        toast.error("Erro ao carregar equipamento");
      }
    },

    async initAssociations() {
      this.tableItems = [];
      this.tablePage = 1;
      this.tableSearch = "";
      this.checkedIds = {};
      this.originalAssociatedIds = new Set();

      try {
        const res = await axios.get(
          `${this.baseUrl}/api/v1/equipamento/associacao/`,
          {
            params: { equipamento_associado_id: this.selectedEquipamento.id },
          },
        );
        const associated = res.data.data || [];
        const ids = {};
        associated.forEach((e) => {
          ids[e.id] = true;
        });
        this.originalAssociatedIds = new Set(associated.map((e) => e.id));
        this.checkedIds = { ...ids };
      } catch {
        // start fresh
      }

      await this.loadTablePage(1);
    },

    async loadTablePage(page) {
      this.tableLoading = true;
      try {
        const params = {
          page,
          page_size: this.tablePageSize,
          search: this.tableSearch || undefined,
        };
        // Append active filters
        Object.entries(this.tableFilters).forEach(([key, val]) => {
          if (val !== "") params[key] = val;
        });

        const res = await axios.get(
          `${this.baseUrl}/api/v1/equipamento/pagination/`,
          { params },
        );
        const items = (res.data.results ?? []).filter(
          (e) => e.id !== this.selectedEquipamento.id,
        );
        if (page === 1) {
          this.tableItems = items;
        } else {
          this.tableItems.push(...items);
        }
        this.tableTotal = Math.max(
          0,
          (res.data.total_count ?? res.data.count ?? 0) - 1,
        );
        this.tablePage = page;
      } catch {
        toast.error("Erro ao carregar equipamentos");
      } finally {
        this.tableLoading = false;
      }
    },

    onTableSearch() {
      clearTimeout(this.searchTimeout);
      this.selectAllMode = false;
      this.searchTimeout = setTimeout(() => {
        this.tableItems = [];
        this.loadTablePage(1);
      }, 300);
    },

    onTableFiltersChanged(filters) {
      this.tableFilters = filters;
      this.tableActiveFilterCount =
        this.$refs.assocFilter?.activeFilterCount?.value ??
        Object.keys(filters).length;
      this.selectAllMode = false;
      this.tableItems = [];
      this.loadTablePage(1);
    },

    loadMore() {
      this.loadTablePage(this.tablePage + 1);
    },

    async loadAll() {
      this.tableLoading = true;
      try {
        const params = { page: 1, page_size: 9999 };
        Object.entries(this.tableFilters).forEach(([k, v]) => {
          if (v !== "") params[k] = v;
        });
        if (this.tableSearch) params.search = this.tableSearch;
        const res = await axios.get(
          `${this.baseUrl}/api/v1/equipamento/pagination/`,
          { params },
        );
        const items = (res.data.results ?? []).filter(
          (e) => e.id !== this.selectedEquipamento.id,
        );
        this.tableItems = items;
        this.tableTotal = items.length;
        this.tablePage = 1;
      } catch {
        toast.error("Erro ao carregar equipamentos");
      } finally {
        this.tableLoading = false;
      }
    },

    toggleAllLoaded() {
      const ids = { ...this.checkedIds };
      const check = !this.allLoadedChecked;
      this.tableItems.forEach((e) => {
        ids[e.id] = check;
      });
      this.checkedIds = ids;
      if (!check) this.selectAllMode = false;
    },

    async selectAllPages() {
      this.loadingAllIds = true;
      try {
        const params = { page: 1, page_size: 9999 };
        Object.entries(this.tableFilters).forEach(([k, v]) => {
          if (v !== "") params[k] = v;
        });
        if (this.tableSearch) params.search = this.tableSearch;
        const res = await axios.get(
          `${this.baseUrl}/api/v1/equipamento/pagination/`,
          { params },
        );
        const items = (res.data.results ?? []).filter(
          (e) => e.id !== this.selectedEquipamento.id,
        );
        const ids = { ...this.checkedIds };
        items.forEach((e) => {
          ids[e.id] = true;
        });
        this.checkedIds = ids;
        this.tableItems = items;
        this.tableTotal = items.length;
        this.tablePage = 1;
        this.selectAllMode = true;
      } catch {
        toast.error("Erro ao selecionar todos");
      } finally {
        this.loadingAllIds = false;
      }
    },

    clearAllPages() {
      const ids = { ...this.checkedIds };
      // Uncheck everything that was loaded (visible items); for full clear reset entirely
      Object.keys(ids).forEach((id) => {
        ids[id] = false;
      });
      this.checkedIds = ids;
      this.selectAllMode = false;
    },

    isChecked(id) {
      return !!this.checkedIds[id];
    },

    toggleCheck(id) {
      if (!this.podeGerir) return;
      this.checkedIds = { ...this.checkedIds, [id]: !this.checkedIds[id] };
    },

    resetChanges() {
      const ids = {};
      this.originalAssociatedIds.forEach((id) => {
        ids[id] = true;
      });
      this.checkedIds = ids;
    },

    async saveChanges() {
      if (!this.podeGerir || !this.hasChanges) return;
      this.savingChanges = true;
      const toAdd = [...this.toAssociate];
      const toRemove = [...this.toDisassociate];
      try {
        const promises = [];
        if (toAdd.length) {
          promises.push(
            axios.post(`${this.baseUrl}/api/v1/equipamento/associacao/`, {
              equipamento_associado_id: this.selectedEquipamento.id,
              equipamentos_ids: toAdd,
            }),
          );
        }
        if (toRemove.length) {
          promises.push(
            axios.delete(`${this.baseUrl}/api/v1/equipamento/associacao/`, {
              data: { equipamentos_ids: toRemove },
            }),
          );
        }
        await Promise.all(promises);

        const newSet = new Set();
        Object.keys(this.checkedIds).forEach((id) => {
          if (this.checkedIds[id]) newSet.add(Number(id));
        });
        this.originalAssociatedIds = newSet;

        const parts = [];
        if (toAdd.length) parts.push(`${toAdd.length} associado(s)`);
        if (toRemove.length) parts.push(`${toRemove.length} removido(s)`);
        toast.success(parts.join(", "));
      } catch {
        toast.error("Erro ao guardar alterações");
      } finally {
        this.savingChanges = false;
      }
    },

    toggleMostrar(field) {
      if (!this.podeGerir) return;
      this.mostrarValues[field] = this.mostrarValues[field] === 1 ? 0 : 1;
    },

    async saveMostrar() {
      if (!this.podeGerir) return;
      this.savingMostrar = true;
      try {
        await axios.patch(
          `${this.baseUrl}/api/v1/equipamento/${this.selectedEquipamento.id}/mostrar/`,
          this.mostrarValues,
        );
        toast.success("Visibilidade atualizada com sucesso");
      } catch {
        toast.error("Erro ao atualizar visibilidade");
      } finally {
        this.savingMostrar = false;
      }
    },

    applyEquipamentoFilters() {
      this.showTableFilters = true;
      this.$nextTick(() => {
        this.$refs.assocFilter?.setFilters(this.selectedEquipamento);
      });
    },

    clearSelection() {
      this.selectedEquipamento = null;
      this.tableItems = [];
      this.checkedIds = {};
      this.originalAssociatedIds = new Set();
      this.tableSearch = "";
    },
  },
};
</script>

<style scoped>
.scrollbar-tema {
  scrollbar-width: thin;
  scrollbar-color: var(--scroll) transparent;
}

.toggle-btn {
  font-size: 0.7rem;
  padding: 0.2rem 0.65rem;
  border-radius: 9999px;
  font-weight: 500;
  transition: background-color 0.15s;
  min-width: 42px;
  text-align: center;
}

.toggle-on {
  background-color: #16a34a;
  color: #fff;
}
.toggle-off {
  background-color: #4b5563;
  color: #d1d5db;
}
.toggle-on:hover {
  background-color: #15803d;
}
.toggle-off:hover {
  background-color: #374151;
}
</style>
